import React from 'react';
import _ from 'lodash';
import {
  CategoryField,
  CategoryChoiceValue,
  CategoryChoice,
} from '@scale/shared/types/params/textcollection';
import {
  CategoryFieldAnnotation,
  TextCollectionAnnotation,
} from '@scale/shared/types/response/textcollection';
import { getHelperText, getHelperDescriptionForMinMaxSelections } from '../../helperText';
import classnames from 'classnames';
import { makeStyles } from 'design-system/styles';
import { Checkbox } from 'design-system';
import { Radio } from 'design-system/Radio';
import { RadioGroup } from 'design-system/RadioGroup';
import { TextField, HelperText, Tooltip, Icon } from '@scale/scaleui';
import HintTooltipIcon from '../../HintTooltipIcon';
import {
  depthFirstChoices,
  getCategoryParentValues,
  getCategoryFullLabelPath,
  getCategoryFullPath,
} from '@scale/shared/textcollection/field';
import Fuse from 'fuse.js';
import {
  getCategoryAnnotationChecked,
  getCategoryAnnotationCheckedGroupLabels,
} from 'react-scale/components/scalers/tasks/textcollection/FieldPreview';
import fetch from 'isomorphic-fetch';
import { Event, logEvent } from 'react-scale/lib/events';
import { areFieldConditionsMet } from '@scale/shared/textcollection/helpers';
import FieldHeader from 'react-scale/components/scalers/tasks/textcollection/SimpleTextCollection/FieldHeader';
import { includesValueTrimmingSpaces } from '../../utils';

interface CategorizationFieldSectionProps {
  field: CategoryField;
  annotation: CategoryFieldAnnotation;
  currentAnnotations: Record<string, TextCollectionAnnotation>;
  setAnnotation: (annotation: CategoryFieldAnnotation) => void;
  disabled?: boolean;
  assignmentId?: string;
}

export default function CategoryFieldSection(props: CategorizationFieldSectionProps) {
  const onChange = function (index: number, values: CategoryChoiceValue[]): void {
    const response = props.annotation.response.slice();
    response[index] = values;
    const annotation: CategoryFieldAnnotation = {
      ...props.annotation,
      response,
    };

    props.setAnnotation(annotation);
  };

  const [field, setField] = React.useState<CategoryField>(props.field);

  React.useEffect(() => {
    setField(props.field);
    if (!props.field.choicesS3Artifact) {
      return;
    }
    const fetchChoicesFromS3 = async () => {
      try {
        const resp = await fetch(props.field.choicesS3Artifact!);
        const respText = await resp.text();
        const choices = JSON.parse(respText);
        const newField = { ...field, choices };

        setField(newField);
      } catch (error) {
        return;
      }
    };
    fetchChoicesFromS3();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.field]);

  if (!props.annotation.response) props.annotation.response = [];
  const isMultipleChoice = (props.field.max_choices || 0) > 1 || field.use_category_choose_multiple;

  const categoryFields = props.annotation.response.map(
    (response: CategoryChoiceValue[], index: number) => {
      return (
        <div key={`${props.field.field_id} ${index}`}>
          {!isMultipleChoice && (
            <CategoryChooseSingle
              response={response}
              field={field}
              updateResponse={newValues => onChange(index, newValues)}
              disabled={props.disabled}
              currentAnnotations={props.currentAnnotations}
            />
          )}
          {isMultipleChoice && (
            <CategoryChooseMultiple
              response={response}
              field={field}
              updateResponse={newValues => onChange(index, newValues)}
              disabled={props.disabled}
              assignmentId={props.assignmentId}
              currentAnnotations={props.currentAnnotations}
            />
          )}
        </div>
      );
    },
  );

  const title = field.title || field.field_id;
  const requiredStr = field.required ? ' *' : '';
  const titleString = `${title}${requiredStr}`;
  const minMaxText = getHelperText({
    description:
      isMultipleChoice && field.hideMinMaxLabel !== true
        ? getHelperDescriptionForMinMaxSelections(field.min_choices, field.max_choices)
        : undefined,
  });
  return (
    <>
      <FieldHeader
        title={titleString}
        description={field.description}
        hint={field.hint}
        rubric={field.rubric}
      />
      {minMaxText && <HelperText>{minMaxText}</HelperText>}
      {categoryFields}
    </>
  );
}

const CategoryChooseSingle: React.FC<{
  field: CategoryField;
  updateResponse: (answer: CategoryChoiceValue[]) => void;
  response: CategoryChoiceValue[];
  disabled?: boolean;
  currentAnnotations: Record<string, TextCollectionAnnotation>;
}> = ({ field, updateResponse, response, disabled, currentAnnotations }) => {
  const classes = useCategorizationFieldSectionStyles();

  const autoComplete = useCategoryAutoComplete(field, false);

  const [visible, setVisible] = React.useState<CategoryChoiceValue[]>(() => {
    return getCategoryParentValues(field.choices, response);
  });

  React.useEffect(() => {
    setVisible(getCategoryParentValues(field.choices, response));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field]);

  const onLeafChange = (value: string) => {
    updateResponse([value]);
    const nextVisible = getCategoryParentValues(field.choices, [value]).concat([value]);
    setVisible(nextVisible);
    autoComplete.clear();
  };

  const canSelectIntermediateNodes = field.can_select_intermediate_nodes;

  const renderChoice = (
    choice: CategoryChoice,
    parentChoices: CategoryChoice[] = [],
  ): React.ReactNode => {
    if (choice.conditions && !areFieldConditionsMet(choice.conditions, currentAnnotations)) {
      return;
    }
    const key = `${field.field_id} ${choice.value}`;
    const label = (
      <ChoiceLabel
        label={choice.label}
        hint={choice.hint || choice.instruction}
        subLabel={choice.subLabel}
        preferred={choice.preferred}
        link={choice.link}
      />
    );

    const getLeafLabel = () => <div className={classes.checkboxLabel}>{label}</div>;

    if (!isParentChoice(choice)) {
      const leafLabel = getLeafLabel();
      return (
        <div style={{ display: 'flex' }}>
          <Radio
            key={key}
            className={classnames(
              classes.choicesRow,
              {
                [classes.highlightNode]: includesValueTrimmingSpaces(response, choice.value),
              },
              'hover:bg-neutral-100',
            )}
            value={choice.value.toString()}
            labelPlacement="end"
            label={leafLabel}
            disabled={disabled}
            radioColor={includesValueTrimmingSpaces(response, choice.value) ? '#6A5DF9' : undefined}
          />
        </div>
      );
    }

    const isVisible = visible.includes(choice.value);

    const onParentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextVisible = new Set<CategoryChoiceValue>();
      for (const parentChoice of parentChoices) {
        nextVisible.add(parentChoice.value);
      }
      if (event.target.checked) {
        nextVisible.add(choice.value);
      }
      if (canSelectIntermediateNodes) {
        onLeafChange(choice.value.toString());
      } else {
        updateResponse([]);
      }
      setVisible(Array.from(nextVisible));
    };

    let parentLabel = label;
    if (canSelectIntermediateNodes) {
      // Add radio button to indicate parent is also selectable
      const leafLabel = getLeafLabel();
      parentLabel = (
        <Radio
          className={classnames(classes.parentChoicesRow, 'hover:bg-neutral-100')}
          radioClassName={classes.parentRadio}
          value={choice.value.toString()}
          labelPlacement="end"
          label={leafLabel}
          disabled={disabled}
        />
      );
    }

    return (
      <React.Fragment key={key}>
        <ParentCheckbox
          checked={isVisible}
          label={parentLabel}
          onChange={onParentChange}
          disabled={disabled}
          canSelectIntermediateNodes={canSelectIntermediateNodes}
          highlightNode={isVisible || includesValueTrimmingSpaces(response, choice.value)}
        />
        {isVisible && _.isArray(choice.subchoices) && (
          <div className={classes.subchoices}>
            {choice.subchoices.map(subchoice =>
              renderChoice(subchoice, [...parentChoices, choice]),
            )}
          </div>
        )}
      </React.Fragment>
    );
  };

  const hasNestedOptions = _.some(field.choices, isParentChoice);
  const searchEnabled = (field.showSearchBar || hasNestedOptions) && !disabled;
  const showSelectedChoices = hasNestedOptions || field.showSearchBar;
  const showChoices = !field.showSearchBar || autoComplete.isActive;
  const anchorRef = React.useRef(null);

  const filterOutEmptyChoices = React.useCallback((choices: CategoryChoice[]) => {
    const filteredChoices = [];
    for (const choice of choices) {
      if (choice.subchoices) {
        choice.subchoices = filterOutEmptyChoices(choice.subchoices);
      }
      if (choice.label.length) {
        filteredChoices.push(choice);
      }
    }
    return filteredChoices;
  }, []);

  const filteredChoices = React.useMemo(() => {
    return filterOutEmptyChoices(autoComplete.choices);
  }, [autoComplete.choices, filterOutEmptyChoices]);

  return (
    <div
      className={classnames(classes.choicesContainer, 'border border-solid border-neutral-300')}
      ref={anchorRef}
    >
      {showSelectedChoices && (
        <div className="m-2 text-neutral-700">
          {'Choice Path: '}
          <span>{getCategoryFullLabelPath(response[0], field)}</span>
        </div>
      )}

      {searchEnabled && (
        <div className={classes.autoCompleteRow}>
          <TextField
            value={autoComplete.pattern}
            onChange={event => autoComplete.search(event.target.value)}
            placeholder="Search choices..."
            onKeyDown={e => {
              if ((e.key === 'Escape' || e.key === 'Tab') && e.target instanceof HTMLElement) {
                e.target.blur();
              }
              e.stopPropagation();
            }}
          />
        </div>
      )}
      {showChoices && (
        <RadioGroup value={response[0]?.toString()} onChange={onLeafChange}>
          {filteredChoices.map(choice => renderChoice(choice, undefined))}
        </RadioGroup>
      )}
    </div>
  );
};

const CategoryChooseMultiple: React.FC<{
  field: CategoryField;
  updateResponse: (answer: CategoryChoiceValue[]) => void;
  response: CategoryChoiceValue[];
  disabled?: boolean;
  assignmentId?: string;
  currentAnnotations: Record<string, TextCollectionAnnotation>;
}> = ({ field, updateResponse, response, disabled, assignmentId, currentAnnotations }) => {
  const classes = useCategorizationFieldSectionStyles();

  const [visible, setVisible] = React.useState<CategoryChoiceValue[]>(() => {
    return getCategoryParentValues(field.choices, response);
  });

  const [searchQueryText, setSearchQueryText] = React.useState<string>('');

  const autoComplete = useCategoryAutoComplete(field, false);

  const responseSetToNodesInPath = (response: Set<CategoryChoiceValue>): string[] => {
    const newNodesInPath: string[] = [];
    response.forEach(choice =>
      newNodesInPath.push(...getCategoryFullPath(field.choices, choice).values),
    );
    return newNodesInPath;
  };

  const [nodesInPath, setNodesInPath] = React.useState<string[]>(() => {
    return responseSetToNodesInPath(new Set(response));
  });

  const groupSameLabelChoices = field.groupSamePathLabelCategories;

  const categoryPathLabelGroups: Record<string, CategoryChoiceValue[]> = React.useMemo(() => {
    const allChoices: CategoryChoice[] = [];
    const allCategoryPathLabelGroups: Record<string, CategoryChoiceValue[]> = {};
    if (!groupSameLabelChoices) return allCategoryPathLabelGroups;
    for (const [choice] of depthFirstChoices(field.choices)) {
      allChoices.push(choice);
    }
    allChoices.forEach(choice => {
      allCategoryPathLabelGroups[choice.label] = [
        ...(allCategoryPathLabelGroups[choice.label] ?? []),
        choice.value,
      ];
    });
    return allCategoryPathLabelGroups;
  }, [field, groupSameLabelChoices]);

  const onLeafChange = async (choice: CategoryChoice, leafChecked: boolean) => {
    const newResponse = new Set(response);
    const allRelevantChoiceValues = groupSameLabelChoices
      ? categoryPathLabelGroups[choice.label]
      : [choice.value];
    if (leafChecked) {
      // Unset relevant choices' parents
      allRelevantChoiceValues.forEach(async choiceValue => {
        const fullPathChoiceValues = getCategoryFullPath(field.choices, choiceValue).values;
        for (const parentChoiceValue of fullPathChoiceValues.slice(0, -1)) {
          newResponse.delete(parentChoiceValue);
        }
        newResponse.add(choiceValue);

        if (searchEnabled) {
          const categorySearchEvent = {
            assignmentId,
            searchQuery: autoComplete.pattern,
            selectedCategory: choiceValue,
            isFuseMatch: choice.isFuseMatch,
          };
          await logEvent(Event.CategorySearchEvent, categorySearchEvent);
        }
      });
    } else {
      allRelevantChoiceValues.forEach(choiceValue => newResponse.delete(choiceValue));
    }
    updateResponse([...newResponse]);
    setNodesInPath(responseSetToNodesInPath(newResponse));
    autoComplete.clear();
    setSearchQueryText('');
  };

  const canSelectIntermediateNodes = field.can_select_intermediate_nodes;

  const renderChoice = (choice: CategoryChoice): React.ReactNode => {
    if (choice.conditions && !areFieldConditionsMet(choice.conditions, currentAnnotations)) {
      return;
    }
    const key = `${field.field_id} ${choice.value}`;
    const label = (
      <ChoiceLabel
        label={choice.label}
        hint={choice.hint || choice.instruction}
        subLabel={choice.subLabel}
        preferred={choice.preferred}
        link={choice.link}
      />
    );

    const getLeafLabel = () => <div className={classes.checkboxLabel}>{label}</div>;

    if (!isParentChoice(choice)) {
      const leafLabel = getLeafLabel();
      return (
        <div style={{ display: 'flex' }}>
          <Checkbox
            key={key}
            className={classnames(
              classes.choicesRow,
              {
                [classes.highlightNode]: nodesInPath.includes(choice.value as string),
              },
              'hover:bg-neutral-100',
            )}
            classes={{
              label: classes.checkboxLabel,
            }}
            checked={includesValueTrimmingSpaces(response, choice.value)}
            labelPlacement="end"
            label={leafLabel}
            onChange={e => onLeafChange(choice, e.target.checked)}
            disabled={disabled}
            checkBoxColor="rgba(106, 93, 249)"
          />
        </div>
      );
    }
    const isVisible = visible.includes(choice.value);
    const onParentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextVisible = new Set(visible);
      if (event.target.checked) {
        nextVisible.add(choice.value);
      } else {
        nextVisible.delete(choice.value);
      }
      setVisible(Array.from(nextVisible));
    };

    let parentLabel = label;
    if (canSelectIntermediateNodes) {
      // Add checkbox to indicate parent is also selectable
      const leafLabel = getLeafLabel();
      parentLabel = (
        <div style={{ display: 'flex' }}>
          <Checkbox
            key={key}
            className={classnames(classes.parentChoicesRow, 'hover:bg-neutral-100')}
            classes={{
              label: classes.checkboxLabel,
            }}
            checked={includesValueTrimmingSpaces(response, choice.value)}
            labelPlacement="end"
            label={leafLabel}
            onChange={e => onLeafChange(choice, e.target.checked)}
            disabled={disabled}
            checkBoxColor="rgba(106, 93, 249)"
          />
        </div>
      );
    }

    return (
      <React.Fragment key={key}>
        <ParentCheckbox
          checked={isVisible}
          label={parentLabel}
          onChange={onParentChange}
          disabled={disabled}
          canSelectIntermediateNodes={canSelectIntermediateNodes}
          highlightNode={nodesInPath.includes(choice.value as string)}
        />
        {isVisible && _.isArray(choice.subchoices) && (
          <div className={classes.subchoices}>
            {choice.subchoices.map(choice => renderChoice(choice))}
          </div>
        )}
      </React.Fragment>
    );
  };

  const hasNestedOptions = _.some(field.choices, isParentChoice);
  const searchEnabled = (field.showSearchBar || hasNestedOptions) && !disabled;
  const showSelectedChoices = hasNestedOptions || field.showSearchBar;
  const showChoices = !field.showSearchBar || autoComplete.isActive;
  const anchorRef = React.useRef(null);

  const debouncedAutoCompleteAction = React.useMemo(
    () =>
      _.debounce((newSearchQuery: string) => {
        autoComplete.search(newSearchQuery);
      }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [autoComplete.search],
  );

  const onAutoCompleteChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSearchQuery = event.target.value;
      setSearchQueryText(newSearchQuery);
      debouncedAutoCompleteAction(newSearchQuery);
    },
    [debouncedAutoCompleteAction],
  );

  return (
    <div
      className={classnames(classes.choicesContainer, 'border border-solid border-neutral-300')}
      ref={anchorRef}
    >
      {showSelectedChoices && (
        <div className="m-2 text-neutral-700">
          {'Choice Paths: '}
          {groupSameLabelChoices
            ? getCategoryAnnotationCheckedGroupLabels(
                response,
                updateResponse,
                field,
                setNodesInPath,
                responseSetToNodesInPath,
                () => {},
                disabled,
              )
            : getCategoryAnnotationChecked(
                response,
                updateResponse,
                field,
                setNodesInPath,
                responseSetToNodesInPath,
                () => {},
                false,
                disabled,
              )}
        </div>
      )}
      {searchEnabled && (
        <div className={classes.autoCompleteRow}>
          <TextField
            value={searchQueryText}
            onChange={onAutoCompleteChange}
            placeholder="Search choices..."
            onKeyDown={e => {
              if ((e.key === 'Escape' || e.key === 'Tab') && e.target instanceof HTMLElement) {
                e.target.blur();
              }
              e.stopPropagation();
            }}
          />
        </div>
      )}
      {showChoices && autoComplete.choices.map(choice => renderChoice(choice))}
    </div>
  );
};

interface ChoiceLabelProps {
  label: string;
  hint?: string;
  subLabel?: string;
  preferred?: boolean;
  link?: string;
}

const ChoiceLabel: React.FC<ChoiceLabelProps> = props => {
  const classes = useCategorizationFieldSectionStyles();
  return (
    <div>
      <div className="flex flex-row items-center">
        <span className={(classnames(classes.label), 'text-neutral-700')}>{props.label}</span>
        {props.hint && (
          <Tooltip
            asChild
            contentClassName="max-w-[500px] whitespace-pre-wrap"
            side="top"
            text={props.hint}
            target={
              <div>
                <Icon icon={['far', 'info-circle']} size="sm" />
              </div>
            }
          />
        )}
        {props.link && <HintTooltipIcon hint={props.link} link={true} />}
      </div>
      {props.subLabel && <div className={classes.subLabel}>{props.subLabel}</div>}
    </div>
  );
};

const ParentCheckbox: React.FC<{
  checked?: boolean;
  label: React.ReactNode;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  disabled?: boolean;
  canSelectIntermediateNodes?: boolean;
  highlightNode?: boolean;
}> = props => {
  const { checked, label, onChange, disabled, canSelectIntermediateNodes, highlightNode } = props;

  const classes = useCategorizationFieldSectionStyles(canSelectIntermediateNodes);

  return (
    <div style={{ display: 'flex' }}>
      <Checkbox
        className={classnames(
          canSelectIntermediateNodes ? classes.parentChoicesRow : classes.choicesRow,
          highlightNode ? classes.highlightNode : classes.unHighlightNode,
          'hover:bg-neutral-100',
        )}
        classes={{ checkboxRoot: classes.checkboxCompact }}
        checked={checked}
        labelPlacement="end"
        label={label}
        onChange={onChange}
        icon={
          <ChevronRightIcon
            className={classnames({
              [classes.parentIconDisabled]: disabled,
            })}
            width="16"
            height="16"
          />
        }
        checkedIcon={
          <ChevronDownIcon
            width="16"
            height="16"
            className={classnames({
              [classes.parentIconExpanded]: !disabled,
              [classes.parentIconDisabled]: disabled,
            })}
          />
        }
        disabled={disabled}
        checkBoxColor="rgba(106, 93, 249)"
      />
    </div>
  );
};

ParentCheckbox.displayName = 'ParentCheckbox';

function isParentChoice(choice: CategoryChoice): boolean {
  return _.size(choice.subchoices) > 0;
}

const useCategorizationFieldSectionStyles = makeStyles(theme => {
  return {
    choicesContainer: {
      borderRadius: 8,
      padding: '8px 0',
      flexGrow: 1,
      maxHeight: '70vh',
      overflowY: 'scroll',
    },
    autoCompleteRow: {
      padding: '0 10px',
      marginBottom: 4,
    },

    choicesRow: {
      padding: '0 10px',
      flexGrow: 1,
      display: 'flex',
    },
    parentChoicesRow: {
      padding: 0,
      flexGrow: 1,
      display: 'flex',
    },
    parentRadio: {
      paddingLeft: 0,
    },
    checkboxLabel: {
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    highlightNode: {
      color: '#6A5DF9',
    },
    unHighlightNode: {
      color: 'black',
    },
    checkboxCompact: (canSelectIntermediateNodes: boolean) => ({
      paddingRight: canSelectIntermediateNodes ? 6 : 'inherit',
    }),
    parentIconDisabled: {
      fill: 'rgba(0, 0, 0, 0.26)',
    },
    parentIconExpanded: {
      fill: '#6A5DF9',
    },
    subchoices: {
      marginLeft: 16,
    },
    label: {
      display: 'flex',
      alignItems: 'center',
      lineHeight: '20px',
      whiteSpace: 'pre-wrap',
    },
    subLabel: {
      fontSize: '11px',
      color: theme.colors.gray50,
    },
  };
});

const ChevronDownIcon: React.FC<React.SVGAttributes<SVGElement>> = props => {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path d="M11.3636 16.7056L4.07572 9.41766C3.72423 9.06617 3.72423 8.49632 4.07572 8.14487L4.92573 7.29486C5.27662 6.94397 5.84531 6.94329 6.19702 7.29336L12 13.0691L17.8029 7.29336C18.1546 6.94329 18.7233 6.94397 19.0742 7.29486L19.9242 8.14487C20.2757 8.49636 20.2757 9.06621 19.9242 9.41766L12.6364 16.7056C12.2849 17.057 11.7151 17.057 11.3636 16.7056Z" />
    </svg>
  );
};

const ChevronRightIcon: React.FC<React.SVGAttributes<SVGElement>> = props => {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path d="M16.7053 12.6348L9.41741 19.9227C9.06593 20.2742 8.49608 20.2742 8.14463 19.9227L7.29461 19.0727C6.94373 18.7218 6.94305 18.1531 7.29311 17.8014L13.0689 11.9984L7.29311 6.19546C6.94305 5.84375 6.94373 5.27506 7.29461 4.92417L8.14463 4.07416C8.49611 3.72267 9.06596 3.72267 9.41741 4.07416L16.7053 11.362C17.0568 11.7135 17.0568 12.2833 16.7053 12.6348Z" />
    </svg>
  );
};

interface CategoryAutoComplete {
  isActive: boolean;
  pattern: string;
  choices: CategoryChoice[];
  search: (pattern: string) => void;
  clear(): void;
}

function useCategoryAutoComplete(
  field: CategoryField,
  excludeLeafNodes?: boolean,
): CategoryAutoComplete {
  const [pattern, setPattern] = React.useState('');

  const choiceNodes = React.useMemo(() => {
    return getChoiceNodesFromTree(field.choices, excludeLeafNodes);
  }, [field.choices, excludeLeafNodes]);

  const searchIndex = React.useMemo(() => {
    return buildCategoryChoiceSearchIndex(choiceNodes);
  }, [choiceNodes]);

  const choices = React.useMemo(() => {
    if (!pattern) {
      return field.choices;
    }

    const fuseScores: Record<string, number> = {};
    if (field.tokenizeSearch) {
      const terms = _.compact(pattern.split(' '));

      for (const term of terms) {
        const searchResults = searchIndex.search(term);
        for (const searchResult of searchResults) {
          const keyName = searchResult.item.choice.label;
          if (keyName in fuseScores) {
            fuseScores[keyName] += 1 - (searchResult.score ?? 1);
          } else {
            fuseScores[keyName] = 1 - (searchResult.score ?? 1);
          }
        }
      }
    } else {
      const fuseResults = searchIndex.search(pattern);
      for (const fuseResult of fuseResults) {
        const keyName = fuseResult.item.choice.label;
        fuseScores[keyName] = 1 - (fuseResult.score ?? 1);
      }
    }

    // Rescore these to match the range of semantic search scores
    const scoredResults: Record<string, number> = {};
    for (const keyName in fuseScores) {
      const fuseScore = fuseScores[keyName];
      // Generally < 0.99 is fuzzy and > 0.99 is exact match
      if (fuseScore < 0.99) {
        // always < semantic results
        scoredResults[keyName] = -2 + fuseScore;
      } else {
        // always > semantic results
        scoredResults[keyName] = 1 + fuseScore;
      }
    }

    const matchedNodes = choiceNodes.filter(
      node => node.choice.label in scoredResults || node.choice.pinned,
    );
    const sortedChoices = [...matchedNodes].sort((choice1, choice2) => {
      // show pinned choices at the top of the list
      if (choice1.choice.pinned) {
        return -1;
      } else if (choice2.choice.pinned) {
        return 1;
      }
      const key1 = choice1.choice.label;
      const key2 = choice2.choice.label;
      return scoredResults[key2] - scoredResults[key1];
    });

    for (const choice of sortedChoices) {
      choice.choice.isFuseMatch = choice.choice.label in fuseScores;
    }

    if (excludeLeafNodes) {
      return _.uniq(_.compact(_.take(sortedChoices, 18).map(result => result.parent)));
    }
    return _.take(sortedChoices, 18).map(result => result.choice);
  }, [pattern, field.tokenizeSearch, field.choices, choiceNodes, excludeLeafNodes, searchIndex]);

  const clear = () => {
    setPattern('');
  };

  return {
    isActive: !!pattern,
    pattern,
    choices,
    search: setPattern,
    clear,
  };
}

function getChoiceNodesFromTree(
  choices: CategoryChoice[],
  excludeLeafNodes?: boolean,
): ChoiceSearchNode[] {
  const choicesWithParents: ChoiceSearchNode[] = [];
  const parentChoices: CategoryChoice[] = [];
  let lastDepth = -1;
  for (const [choice, depth] of depthFirstChoices(choices)) {
    const drop = 1 + lastDepth - depth;
    parentChoices.splice(parentChoices.length - drop, drop);
    const parent = parentChoices.length > 0 ? parentChoices[parentChoices.length - 1] : undefined;
    if (!excludeLeafNodes || _.isEmpty(choice.subchoices)) {
      choicesWithParents.push({ choice, parent });
    }
    parentChoices.push(choice);
    lastDepth = depth;
  }
  return choicesWithParents;
}

function buildCategoryChoiceSearchIndex(choiceNodes: ChoiceSearchNode[]): Fuse<ChoiceSearchNode> {
  const searchIndex = new Fuse(choiceNodes, {
    keys: [
      { name: 'choice.label', weight: 1 },
      { name: 'choice.hint', weight: 0.5 },
    ],
    threshold: 0.4,
    ignoreLocation: true,
    ignoreFieldNorm: true,
    includeScore: true,
  });

  return searchIndex;
}

interface ChoiceSearchNode {
  choice: CategoryChoice;
  parent?: CategoryChoice;
}
